import parsePriceMixin from './parsePriceParams.js';
import parseServicesMixin from './parseServicesParams.js';
import parseFeaturesMixin from './parseFeaturesParams.js';
import parseCategoryMixin from './parseCategoryParams.js';
import parseLocationMixin from './parseLocationParams.js';
import SEO_URL_QUERY_KEYS from "@/core/constants/_seo_url_query_keys";
import parseExtraContentParamsMixin from "@/components/mixins/parseExtraContentParams";
import {searchLocationPayload} from "@/core/utils/helpers/search/searchLocationPayloadHelper";

async function generateUrl() {
    const payload = {
        location: searchLocationPayload(this.location),
        price: this.price,
        features: this.features,
        services: this.services,
        category: this.category,
        extraContent: this.extraContent,
        venue: this.venue,
        asia: this.asia,
    };

    const [err, response] = await to(
        api.seo.generateUrl(payload)
    );

    if (err) {
        toastr.error('Something went wrong with url!');
    }

    let path = _.get(response, 'data.path', '');
    let slug = _.get(response, 'data.slug', '');
    let postcode = _.get(response, 'data.postcode', '');
    let h1 = _.get(response, 'data.h1', '');
    let query = _.get(response, 'data.query', '');
    let title_tag = _.get(response, 'data.title_tag', '');
    let publicCategories = _.get(response, 'data.publicCategories', []);
    let nearbyLocations = _.get(response, 'data.nearbyLocations', []);
    let radius = _.get(response, 'data.radius', 20);
    let adverts = _.get(response, 'data.adverts', {});

    let hash = await this.generateHashForUrl(path);

    if (slug.length) {
        hash.location = slug;
    }

    if (postcode.length) {
        hash.postcode = postcode;
    }

    if (Object.keys(hash).length) {
        path += '#' + utils.params(hash);
        path = path.replace(/%2C/g, ',');
    }

    const tmp = _.cloneDeep(this.location);
    tmp.h1 = h1;
    tmp.breadcrumbs = _.get(response, 'data.breadcrumbs', []);
    tmp.query = query;
    tmp.fromMain = true;
    tmp.title_tag = title_tag;
    tmp.pathname = path;
    tmp.adverts = adverts;

    if (!this.searchRadius || (!tmp.radius && tmp.radius !== 0)) {
        this.store.commit('setSearchRadius', `${radius}km`);
    }

    tmp.radius = radius;
    tmp.publicCategories = publicCategories;
    tmp.nearbyLocations = nearbyLocations;

    document.title = title_tag;
    this.store.commit('setLocation', tmp);

    return path;
}

function fetchDataFromLocalStorage() {
    const location = JSON.parse(localStorage.getItem('location'));
    const price = JSON.parse(localStorage.getItem('price'));
    const features = JSON.parse(localStorage.getItem('features'));
    const services = JSON.parse(localStorage.getItem('services'));
    const category = JSON.parse(localStorage.getItem('category'));
    const searchResponse = JSON.parse(localStorage.getItem('searchResponse'));
    const allServices = JSON.parse(localStorage.getItem('allServices'));
    const searchScrollPosition = JSON.parse(localStorage.getItem('searchScrollPosition'));
    const searchRadius = localStorage.getItem('searchRadius');
    const similarEscorts = JSON.parse(localStorage.getItem('similarEscorts'));

    this.store.commit('setLocation', location);
    this.store.commit('setPrice', price);
    this.store.commit('setFeatures', features);
    this.store.commit('setServices', services);
    this.store.commit('setCategory', category);
    this.store.commit('setSearchResponse', searchResponse);
    this.store.commit('setAllServices', allServices);
    this.store.commit('setSearchScrollPosition', searchScrollPosition);
    this.store.commit('setSearchRadius', searchRadius);
    this.store.commit('setSimilarEscorts', similarEscorts);

    this.removeSavedResultsFromLocalStorage();
}

async function changeUrl() {
    try {
        if (localStorage && localStorage.getItem('last_search_url')) {
            if (_.isEqual(localStorage.getItem('last_search_url'), window.location.href)) {
                this.fetchDataFromLocalStorage();
                return true;
            } else {
                this.removeSavedResultsFromLocalStorage();
            }
        }
    } catch (e) {
    }

    const searchParams = utils.getParamsFromUrlHash();

    if (searchParams[SEO_URL_QUERY_KEYS.GALLERY]) {
        searchParams.privateGallery = true;
    }

    await this.parsePriceParams(searchParams);
    await this.parseServicesParams(searchParams);
    await this.parseFeaturesParams(searchParams);
    await this.parseCategoryParams(searchParams);
    await this.parseExtraContentParams(searchParams);
    await this.parseLocationParams(searchParams);

    this.store.commit('setSearchRadius', _.get(searchParams, 'search_radius', `${_.get(this.location, 'radius', 20)}km`));

    return false;
}

function getPageFromUrlHash() {
    const searchParams = utils.getParamsFromUrlHash();
    let page = _.get(searchParams, 'page', 1);
    if (!/^\d*$/.test(page)) { // only positive integer
        page = 1;
    }

    page = parseInt(page, 10);
    const hasSearchResults = _.get(this, 'searchResponse.users.length', 0) > 0;
    const lastPage = _.get(this, 'searchResponse.last_page', page);

    if (hasSearchResults && (lastPage < page)) {
        return lastPage;
    }

    return page;
}

async function generateHashForUrl(path) {
    let price = await this.generatePriceParams();
    let services = await this.generateServicesParams(path);
    let features = await this.generateFeaturesParams(path);
    let categories = await this.generateCategoriesParams(path);
    let extraContent = await this.generateExtraContentParams(path);

    const result = {
        ...price,
        ...categories,
        ...features,
        ...extraContent,
    };

    if (services.length) {
        result[SEO_URL_QUERY_KEYS.SERVICES] = services;
    }

    // radius can be 0
    const setLocationRadius = this.location.radius != null && this.searchRadius != null && this.searchRadius !== `${this.location.radius}km`;
    if (setLocationRadius) {
        result[SEO_URL_QUERY_KEYS.SEARCH_RADIUS] = this.searchRadius;
    }

    if(_.has(result, 'privateGallery')){
        if (result.privateGallery === true) {
            result[SEO_URL_QUERY_KEYS.GALLERY] = true;
        }

        delete result.privateGallery;
    }

    return _.pickBy(result, value => {
        return (
            value !== null
            && value !== undefined
            && value !== -1
            && _.get(value, 'length', 1) !== 0
        );
    });
}

function addPageToUrlPath(path, page) {
    if (path.includes('#')) {
        if (path.includes('page=')) {
            if (page > 1) {
                return path.replace(/([?&]page=)(\w*)/, '$1' + page);
            } else {
                return path.replace(/(#?[?&]page=)-?\w*/, '');
            }
        }

        if (page > 1) {
            return path + '&page=' + page;
        }
    }
    if (page > 1) {
        return path + '#?page=' + page;
    }
    return path;
}

async function toggleOnlyPrivateGallerySearch() {
    if (typeof this.$route === "undefined") {
        return;
    }

    const searchOnlyWithPrivateGallery = !_.get(this,'extraContent.privateGallery', false);
    this.store.commit('setSearchOnlyWithPrivateGallery', searchOnlyWithPrivateGallery)

    let path = _.get(this, '$route.path', '')
    let hash = await this.generateHashForUrl(path);

    if (Object.keys(hash).length) {
        path += '#' + utils.params(hash);
        path = path.replace(/%2C/g, ',');
    }

    if (path) {
        this.$router.push({path: path})
        this.store.commit('refreshSearch', false)
    }
}

async function toggleOnlySexChatSearch() {
    if (typeof this.$route === "undefined") {
        return;
    }

    const searchOnlySexChat = !_.get(this, 'extraContent.sexChat', false);
    this.store.commit('setSearchOnlySexChat', searchOnlySexChat);

    let path = _.get(this, '$route.path', '');
    let hash = await this.generateHashForUrl(path);

    if (Object.keys(hash).length) {
        path += '#' + utils.params(hash);
        path = path.replace(/%2C/g, ',');
    }

    if (path) {
        this.$router.push({path: path});
        this.store.commit('refreshSearch', false);
    }
}

function removeSavedResultsFromLocalStorage() {
    localStorage.removeItem('location');
    localStorage.removeItem('price');
    localStorage.removeItem('features');
    localStorage.removeItem('services');
    localStorage.removeItem('category');
    localStorage.removeItem('extraContent');
    localStorage.removeItem('searchResponse');
    localStorage.removeItem('allServices');
    localStorage.removeItem('searchScrollPosition');
    localStorage.removeItem('last_search_url');
    localStorage.removeItem('searchRadius');
    localStorage.removeItem('similarEscorts');
}

function setSearchDataToLocalStorage() {
    if (localStorage) {
        localStorage.setItem('location', JSON.stringify(this.location));
        localStorage.setItem('price', JSON.stringify(this.price));
        localStorage.setItem('features', JSON.stringify(this.features));
        localStorage.setItem('services', JSON.stringify(this.services));
        localStorage.setItem('category', JSON.stringify(this.category));
        localStorage.setItem('extraContent', JSON.stringify(this.extraContent));
        localStorage.setItem('searchResponse', JSON.stringify(this.searchResponse));
        localStorage.setItem('allServices', JSON.stringify(this.allServices));
        localStorage.setItem('searchScrollPosition', JSON.stringify(this.searchScrollPosition));
        localStorage.setItem('current_page', this.currentPage);
        localStorage.setItem('last_page', this.lastPage);
        localStorage.setItem('last_search_url', window.location.href);
        localStorage.setItem('searchRadius', this.searchRadius);
        localStorage.setItem('similarEscorts', JSON.stringify(this.similarEscorts));
    }
}

const data = () => ({
    store: $store,
});

export default {
    data,
    mixins: [
        parsePriceMixin,
        parseServicesMixin,
        parseFeaturesMixin,
        parseCategoryMixin,
        parseLocationMixin,
        parseExtraContentParamsMixin,
    ],
    methods: {
        generateUrl,
        changeUrl,
        addPageToUrlPath,
        getPageFromUrlHash,
        generateHashForUrl,
        fetchDataFromLocalStorage,
        removeSavedResultsFromLocalStorage,
        setSearchDataToLocalStorage,
        toggleOnlyPrivateGallerySearch,
        toggleOnlySexChatSearch,
    },
    computed: {
        ...mapState([
            'translate',
            'location',
            'price',
            'features',
            'services',
            'category',
            'extraContent',
            'priceTemp',
            'featuresTemp',
            'servicesTemp',
            'categoryTemp',
            'extraContentTemp',
            'searchResponse',
            'allServices',
            'locationTemp',
            'searchScrollPosition',
            'searchRadius',
            'searchRadiusTemp',
            'similarEscorts',
            'venue',
        ])
    },
}
